 "use client";

import React from "react";
import { FaWhatsapp } from "react-icons/fa";
import { Mail, Minus, Plus, Loader2, Heart, ShoppingBag, Bell } from "lucide-react";
import type { Product } from "./ProductClientView";

interface NavLink { id: string; label: string; icon: any }

interface StickyBarProps {
  product: Product;
  displayPrice: number;
  hasDiscount: boolean;
  isOutOfStock: boolean;
  isAddingToCart: boolean;
  quantity: number;
  stockQuantity: number;
  handleQuantityChange: (action: 'increase' | 'decrease') => void;
  handleAddToCart: () => void;
  openNotifyModal: (id: string, name?: string) => void;
  mainProductWhatsappUrl: string;
  formatPrice: (p: number) => string;
  navLinks: NavLink[];
  activeSection: string;
  onAnchorClick: (id: string) => void;
  toggleWish: (item: { id: string; name?: string; price?: number; image?: string; slug?: string; parentCategoryName?: string }) => void;
  isWished: (id: string) => boolean;
}

export default function ProductStickyBar({
  product,
  displayPrice,
  hasDiscount,
  isOutOfStock,
  isAddingToCart,
  quantity,
  stockQuantity,
  handleQuantityChange,
  handleAddToCart,
  openNotifyModal,
  mainProductWhatsappUrl,
  formatPrice,
  navLinks,
  activeSection,
  onAnchorClick,
  toggleWish,
  isWished,
}: StickyBarProps) {
  
  const wished = typeof isWished === 'function' && isWished(product.id);

  return (
    <div className="fixed bottom-0 left-0 right-0 z-10 bg-white/80 backdrop-blur-xl border-t border-slate-200/60 shadow-[0_-10px_25px_-5px_rgba(0,0,0,0.05)]">
      <div className="container max-w-7xl mx-auto px-4 md:px-6 py-3">
        
        {/* VIEW MOBILE */}
        <div className="md:hidden flex items-center justify-between gap-3">
          <div className="flex flex-col">
            <span className="text-[10px] uppercase tracking-wider font-bold text-slate-400">Prix</span>
            <div className="flex items-center gap-2">
              <span className="text-xl font-black text-slate-900 leading-none">
                {formatPrice(displayPrice)}
              </span>
              {hasDiscount && (
                <span className="text-xs text-slate-400 line-through decoration-red-400/50">
                  {formatPrice(product.price)}
                </span>
              )}
            </div>
          </div>

          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => toggleWish({ id: product.id, name: product.name, price: displayPrice, image: product.images?.[0], slug: product.slug, parentCategoryName: product.category?.name })}
              className={`w-11 h-11 flex items-center justify-center rounded-2xl transition-all border ${
                wished ? 'bg-rose-50 border-rose-100 text-rose-500' : 'bg-main border-slate-100 text-slate-400'
              }`}
            >
              <Heart className={`w-5 h-5 ${wished ? 'fill-current' : ''}`} />
            </button>

            {isOutOfStock ? (
              <button 
                onClick={() => openNotifyModal(product.id, product.name)}
                className="bg-slate-900 text-white h-11 px-5 rounded-2xl font-bold flex items-center gap-2 active:scale-95 transition-transform"
              >
                <Bell className="w-4 h-4" />
                Alerte
              </button>
            ) : (
              <button
                className="bg-slate-900 text-white h-11 px-6 rounded-2xl font-bold flex items-center gap-2 active:scale-95 transition-all shadow-lg shadow-slate-200 disabled:opacity-70"
                onClick={handleAddToCart}
                disabled={isAddingToCart}
              >
                {isAddingToCart ? <Loader2 className="w-4 h-4 animate-spin" /> : <ShoppingBag className="w-4 h-4" />}
                <span>Panier</span>
              </button>
            )}
          </div>
        </div>

        {/* VIEW DESKTOP */}
        <div className="hidden md:flex items-center justify-between">
          
          {/* Menu de navigation ancré */}
          <div className="flex items-center bg-slate-100/50 p-1 rounded-2xl border border-slate-200/50">
            {navLinks.map((link) => {
              const isActive = activeSection === link.id;
              return (
                <button 
                  key={link.id} 
                  onClick={() => onAnchorClick(link.id)}
                  className={`flex items-center gap-2 px-5 py-2.5 rounded-xl transition-all duration-300 font-bold text-sm ${
                    isActive 
                    ? 'bg-white text-slate-900 shadow-sm' 
                    : 'text-slate-500 hover:text-slate-800'
                  }`}
                >
                  <link.icon className={`w-4 h-4 ${isActive ? 'text-slate-900' : 'text-slate-400'}`} />
                  {link.label}
                </button>
              );
            })}
          </div>

          {/* Actions & Prix */}
          <div className="flex items-center gap-6">
            <div className="flex flex-col items-end">
              {hasDiscount && (
                <span className="text-xs font-bold text-slate-400 line-through decoration-red-400/40">
                  {formatPrice(product.price)}
                </span>
              )}
              <span className="text-2xl font-black text-slate-900">
                {formatPrice(displayPrice)}
              </span>
            </div>

            {isOutOfStock ? (
              <div className="flex items-center gap-3">
                <a 
                  href={mainProductWhatsappUrl} 
                  target="_blank" 
                  rel="noopener noreferrer" 
                  className="bg-emerald-500 hover:bg-emerald-600 text-white w-12 h-12 flex items-center justify-center rounded-2xl transition-all shadow-lg shadow-emerald-100"
                  title="Contact WhatsApp"
                >
                  <FaWhatsapp className="w-6 h-6" />
                </a>
                <button 
                  onClick={() => openNotifyModal(product.id, product.name)} 
                  className="bg-slate-900 text-white h-12 px-6 rounded-2xl font-bold flex items-center gap-2 hover:bg-slate-800 transition-all"
                >
                  <Bell className="w-4 h-4" />
                  M'avertir du stock
                </button>
              </div>
            ) : (
              <div className="flex items-center gap-3">
                {/* Quantity Selector */}
                <div className="flex items-center bg-slate-50 border border-slate-200 p-1 rounded-2xl">
                  <button 
                    onClick={() => handleQuantityChange('decrease')} 
                    disabled={quantity <= 1 || isAddingToCart}
                    className="w-10 h-10 flex items-center justify-center rounded-xl hover:bg-white transition-colors disabled:opacity-30"
                  >
                    <Minus className="w-4 h-4 text-slate-600" />
                  </button>
                  <span className="w-8 text-center font-black text-slate-800 text-lg">{quantity}</span>
                  <button 
                    onClick={() => handleQuantityChange('increase')} 
                    disabled={quantity >= stockQuantity || isAddingToCart}
                    className="w-10 h-10 flex items-center justify-center rounded-xl hover:bg-white transition-colors disabled:opacity-30"
                  >
                    <Plus className="w-4 h-4 text-slate-600" />
                  </button>
                </div>

                {/* Add to Cart Button */}
                <button 
                  className="bg-slate-900 text-white h-12 px-8 rounded-2xl font-bold flex items-center gap-3 hover:bg-slate-800 transition-all shadow-xl shadow-slate-200 disabled:opacity-70 active:scale-95"
                  onClick={handleAddToCart} 
                  disabled={isAddingToCart}
                >
                  {isAddingToCart ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                  ) : (
                    <ShoppingBag className="w-5 h-5" />
                  )}
                  <span>Ajouter au panier</span>
                </button>

                {/* Wishlist Button */}
                <button
                  type="button"
                  onClick={() => toggleWish({ id: product.id, name: product.name, price: displayPrice, image: product.images?.[0], slug: product.slug, parentCategoryName: product.category?.name })}
                  className={`w-12 h-12 flex items-center justify-center rounded-2xl border transition-all ${
                    wished 
                    ? 'bg-rose-50 border-rose-100 text-rose-500' 
                    : 'bg-white border-slate-200 text-slate-400 hover:border-slate-300 hover:text-slate-600'
                  }`}
                >
                  <Heart className={`w-5 h-5 ${wished ? 'fill-current' : ''}`} />
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}